import { useAssessmentAnalytics } from '@schooly/analytics';
import {
  AssessmentEntryComment,
  AssessmentMethodComment,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentWithEntries,
} from '@schooly/api';
import { CommentTableCell, GridCell } from '@schooly/style';
import { FC, useCallback } from 'react';

import { UpdateAssessmentEntryParams } from '../../../pages/Assessments/Markbook/common/types';
import { AssessmentEntryMethod } from '../AssessmentEntryMethods/AssessmentEntryMethod';
import { getAssessmentParams } from '../AssessmentEntryMethods/AssessmentEntryMethod';

const METHOD_CELL_WIDTH = 112;

type ListViewAssessmentsRowContentEntriesProps = {
  assessment: AssessmentWithEntries;
  lists?: Record<string, AssessmentsGrade>;
  relationId: string;
  groupId: string;
  commentMethod: AssessmentMethodComment | undefined;
  isCommentsOpen: boolean;
  onCloseComments?: (id: string) => void;
  onOpenComments?: (id: string) => void;
  localEntries?: AssessmentEntryComment[];
  hasComments: boolean;
  wideComments: boolean;
};

export const ListViewAssessmentsRowContentEntries: FC<
  ListViewAssessmentsRowContentEntriesProps
> = ({
  assessment,
  lists,
  relationId,
  wideComments,
  groupId,
  commentMethod,
  isCommentsOpen,
  onCloseComments,
  onOpenComments,
  localEntries,
  hasComments,
}) => {
  const analyticsContext = useAssessmentAnalytics();
  const canAdd = Boolean(commentMethod?.id && assessment.enterable_and_my_group);
  const canOpen = canAdd || hasComments;

  const onCommentsIconClick = useCallback(() => {
    if (!canOpen) return;
    if (isCommentsOpen) {
      return onCloseComments?.(assessment.id);
    }

    if (analyticsContext) {
      analyticsContext.trackAssessmentOpenComment({
        assessment,
        groupId,
      });
    }

    return onOpenComments?.(assessment.id);
  }, [
    analyticsContext,
    assessment,
    canOpen,
    groupId,
    isCommentsOpen,
    onCloseComments,
    onOpenComments,
  ]);

  const wideCommentControllerProps = {
    entries: localEntries,
    iconOnly: true,
    open: isCommentsOpen,
    onClick: onCommentsIconClick,
    onFocus: () => {},
    onBlur: () => {},
  };

  const trackAssessmentEntryUpdate = useCallback(
    (update: UpdateAssessmentEntryParams) => {
      if (!analyticsContext) return;
      analyticsContext.trackAssessmentEntryUpdate({
        assessment,
        update,
      });
    },
    [analyticsContext, assessment],
  );

  return (
    <>
      {assessment.methods.map((method) => {
        const isEmpty = !method.id;
        const isMethodComment = method.method_type === AssessmentMethodType.Comment;
        const students = assessment.groups[0]?.students ?? [];
        const canComment = students.some((student) => student.relation_id === relationId);

        if (isMethodComment && !canComment) {
          return null;
        }

        const params = getAssessmentParams(relationId, method, assessment);
        if (!params) return null;

        if (isMethodComment) {
          return (
            <CommentTableCell
              key={method.id}
              borderLeft
              borderTopRightRadius
              borderBottomRightRadius
              empty={isEmpty}
              sx={(theme) => ({
                minWidth: METHOD_CELL_WIDTH,
                height: '100%',
                '.GridRow:hover &, .GridRowItem:hover &': {
                  backgroundColor: theme.palette.background.default,
                },
              })}
            >
              {(onToggle, cellRef) => (
                <AssessmentEntryMethod
                  method={method}
                  assessmentId={assessment.id}
                  enterable={assessment.enterable_and_my_group}
                  lists={lists}
                  relationId={relationId}
                  groupId={groupId}
                  updateAssessmentEntry={trackAssessmentEntryUpdate}
                  commentControllerProps={
                    wideComments
                      ? wideCommentControllerProps
                      : {
                          onToggle: (val) => {
                            onToggle(val);

                            if (analyticsContext && !!val) {
                              analyticsContext.trackAssessmentOpenComment({
                                assessment,
                                groupId,
                              });
                            }
                          },
                          getParentRef: () => cellRef,
                          tableView: true,
                          updateAssessmentEntry: trackAssessmentEntryUpdate,
                        }
                  }
                  entries={params.entries}
                />
              )}
            </CommentTableCell>
          );
        }

        return (
          <GridCell
            key={method.id}
            borderLeft
            noPadding
            empty={isEmpty}
            sx={(theme) => ({
              minWidth: METHOD_CELL_WIDTH,
              height: '100%',
              '.GridRow:hover &, .GridRowItem:hover &': {
                backgroundColor: theme.palette.background.default,
              },
            })}
          >
            <AssessmentEntryMethod
              method={method}
              assessmentId={assessment.id}
              enterable={assessment.enterable_and_my_group}
              lists={lists}
              relationId={relationId}
              groupId={groupId}
              commentControllerProps={
                isMethodComment && wideComments ? wideCommentControllerProps : undefined
              }
              entries={params.entries}
              updateAssessmentEntry={(update) => {
                const entry = params.entries[0];
                if (analyticsContext) {
                  analyticsContext.trackAssessmentEntryUpdate({
                    assessment,
                    update,
                    entry,
                  });
                }
              }}
            />
          </GridCell>
        );
      })}
    </>
  );
};
